import clsx from "clsx";
import { useRef, useState, useEffect, memo, useMemo } from "react";
import PhoneValidation, {
	PhoneValidationData,
} from "@/components/forms/consultationForm/parts/phoneValidation";
import { stateOption } from "@/lib/constants";
import { EnvelopeIcon } from "@phosphor-icons/react";
import { FormHeader, ToggleSwitch } from "@/components/ui";
import { secureInput } from "@/lib/security";
import { useFormStateStore } from "@/components/context/useFormContext";

export interface DetailsFormData {
	firstName: string;
	lastName: string;
	email: string;
	phone: PhoneValidationData["phone"];
	phoneVerified: PhoneValidationData["phoneVerified"];
	phoneStage: PhoneValidationData["phoneStage"];
	state: string;
	postcode: string;
	tc: boolean;
	message: string;
}

export interface DetailsFormErrors {
	firstName: boolean;
	lastName: boolean;
	email: boolean;
	phone: boolean;
	phoneVerified: boolean;
	state: boolean;
	postcode: boolean;
	tc: boolean;
	message: boolean;
}

interface FormDataProps {
	formData: DetailsFormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: DetailsFormErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
	enabledTCstatus: boolean;
	setEnabledTCstatus: React.Dispatch<React.SetStateAction<boolean>>;
}

function DetailsForm({
	formData,
	setFormData,
	formErrors,
	setFormErrors,
	enabledTCstatus,
	setEnabledTCstatus,
}: FormDataProps) {
	const { selectedIndex } = useFormStateStore();
	// const [enabledTC, setEnabledTC] = useState(enabledTCstatus);
	const headingRef = useRef<HTMLHeadingElement>(null);

	// Phone Data
	const phoneFormData = useMemo(
		() => ({
			phone: formData.phone,
			phoneVerified: formData.phoneVerified,
			phoneStage: formData.phoneStage,
		}),
		[formData.phone, formData.phoneVerified, formData.phoneStage]
	);
	const phoneFormErrors = useMemo(
		() => ({
			phone: formErrors.phone,
			phoneVerified: formErrors.phoneVerified,
		}),
		[formErrors.phone, formErrors.phoneVerified]
	);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		let isValid: boolean;
		let sanitized: string;

		if (name === "email") {
			({ isValid, sanitized } = secureInput.email(value));
		} else if (name === "postcode") {
			({ isValid, sanitized } = secureInput.postcode(value));
		} else if (name === "message") {
			({ isValid, sanitized } = secureInput.textarea(value));
		} else {
			({ isValid, sanitized } = secureInput.name(value));
		}

		setFormData((prevData: any) => ({ ...prevData, [name]: sanitized }));
		setFormErrors((prevErrors: any) => ({
			...prevErrors,
			[name]: !isValid,
		}));
	};

	useEffect(() => {
		setFormData((prevData: any) => ({ ...prevData, tc: enabledTCstatus }));

		if (formErrors.tc) {
			setFormErrors((prevErrors: any) => ({
				...prevErrors,
				tc: !enabledTCstatus,
			}));
		}
	}, [enabledTCstatus]);

	return (
		<div className="yF-flex yF-flex-col yF-space-y-2">
			<FormHeader
				formType="consultation"
				icon="user"
				title="Your Details"
				description="Tell us a bit about yourself."
				headingRef={headingRef}
			/>
			<div className="yF-flex yF-flex-col yF-space-y-2">
				{/* Row 1: First Name and Last Name */}
				<div className="yF-flex yF-flex-col yF-mt-4 yF-space-y-2 sm:yF-flex-row sm:yF-gap-4 sm:yF-space-y-0">
					{/* First Name */}
					<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
						<label htmlFor="firstName" className="yF-label">
							First Name
						</label>
						<input
							id="firstName"
							name="firstName"
							aria-label="First Name"
							value={formData.firstName}
							onChange={(e) => {
								handleChange(e);
							}}
							autoComplete="true"
							placeholder="First Name"
							maxLength={50}
							inputMode="text"
							className={clsx(
								formErrors.firstName ? "yF-input-error" : "yF-input-focus",
								"yF-input-default"
							)}
						/>
						{formErrors.firstName && (
							<p className="yF-error-text">First Name is required</p>
						)}
					</div>

					{/* Last Name */}
					<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
						<label htmlFor="lastName" className="yF-label">
							Last Name
						</label>
						<input
							id="lastName"
							name="lastName"
							value={formData.lastName}
							onChange={(e) => handleChange(e)}
							autoComplete="true"
							placeholder="Last Name"
							maxLength={50}
							inputMode="text"
							className={clsx(
								formErrors.lastName ? "yF-input-error" : "yF-input-focus",
								"yF-input-default"
							)}
						/>
						{formErrors.lastName && (
							<p className="yF-error-text">Last Name is required</p>
						)}
					</div>
				</div>
			</div>

			{/* Row 2: Email and Phone */}
			<div className="yF-flex yF-flex-col yF-space-y-4 sm:yF-flex-row sm:yF-gap-4 sm:yF-space-y-0">
				{/* Email */}
				<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
					<label htmlFor="email" className="yF-label">
						Email <span className="yF-italic">(optional)</span>
					</label>
					<div className="yF-relative">
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
							<EnvelopeIcon
								className="yF-h-5 yF-w-5 yF-text-gray-400"
								aria-hidden="true"
							/>
						</div>
						<input
							value={formData.email}
							onChange={(e) => {
								handleChange(e);
							}}
							aria-label="Email Address"
							type="email"
							id="email"
							name="email"
							placeholder="Email Address"
							maxLength={50}
							autoComplete="true"
							className={clsx(
								formErrors.email && formData.email.length > 0
									? "yF-input-error"
									: "yF-input-focus",
								"yF-input-default yF-pl-10"
							)}
						/>
					</div>
					{formErrors.email && formData.email.length > 0 && (
						<p className="yF-error-text">Enter a valid email</p>
					)}
				</div>
				{/* Phone */}
				<div className="yF-flex-1 yF-flex yF-flex-col">
					<PhoneValidation
						formData={phoneFormData}
						setFormData={setFormData}
						formErrors={phoneFormErrors}
						setFormErrors={setFormErrors}
					/>
				</div>
			</div>

			{/* Row 3: State and Postcode */}
			<div className="yF-flex yF-flex-col yF-space-y-4 sm:yF-flex-row sm:yF-gap-4 sm:yF-space-y-0">
				{/* State */}
				<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
					<label htmlFor="state" className="yF-label">
						State
					</label>
					<select
						value={formData.state}
						onChange={(e) => {
							handleChange(e);
						}}
						id="state"
						name="state"
						aria-label="State"
						className={clsx(
							formErrors.state ? "yF-select-error" : "yF-select-focus",
							"yF-select-default"
						)}
					>
						{stateOption.map((stateOptions) => (
							<option key={stateOptions.value} value={stateOptions.value}>
								{stateOptions.option}
							</option>
						))}
					</select>
					{formErrors.state && <p className="yF-error-text">State is required</p>}
				</div>
				{/* Postcode */}
				<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
					<label htmlFor="postcode" className="yF-label">
						Postcode
					</label>
					<input
						value={formData.postcode}
						onChange={(e) => {
							handleChange(e);
						}}
						type="tel"
						aria-describedby="postcode"
						aria-label="Postcode"
						id="postcode"
						name="postcode"
						inputMode="numeric"
						placeholder="Postcode"
						maxLength={4}
						autoComplete="true"
						className={clsx(
							formErrors.postcode ? "yF-input-error" : "yF-input-focus",
							"yF-input-default"
						)}
					/>
					{formErrors.postcode && (
						<p className="yF-error-text">Enter a valid 4-digit postcode</p>
					)}
				</div>
			</div>

			{/* Row 4: Message */}
			<div className="yF-flex-1 yF-flex yF-flex-col yF-space-y-2">
				<label htmlFor="message" className="yF-label">
					Message
				</label>
				<textarea
					value={formData.message}
					onChange={(e) => {
						handleChange(e);
					}}
					id="message"
					name="message"
					aria-label="Message"
					placeholder="Brief description of your financial goals or questions..."
					className={clsx(
						formErrors.message ? "yF-input-error" : "yF-input-focus",
						"yF-input-default"
					)}
				/>
				{formErrors.message && formData.message.length <= 250 && (
					<p className="yF-error-text">Invalid characters in message</p>
				)}
				{formErrors.message && formData.message.length > 250 && (
					<p className="yF-error-text">Message has exceeded the 250 character limit</p>
				)}
			</div>

			{/* Row 5: Toggle for Newsletter */}
			<div id="tc-toggle" className={"yF-m-auto yF-text-center !yF-mt-4"} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTCstatus} setEnabled={setEnabledTCstatus} />
				{formErrors.tc && (
					<span className="yF-error-text">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>

			{/* Row 6: Submit Button */}
			{selectedIndex === 2 ? (
				<></>
			) : (
				<div className="yF-m-auto !yF-my-4 yF-w-full md:yF-w-auto">
					<button
						type="submit"
						className={clsx(
							"yF-bg-[--yF-theme-color] yF-text-white yF-text-md md:yF-text-base yF-font-medium yF-px-8 yF-py-4 md:yF-py-2 yF-rounded-md yF-w-full md:yF-w-auto yF-transition-all",
							!enabledTCstatus
								? "yF-bg-gray-400 yF-text-gray-500 yF-cursor-not-allowed"
								: "hover:yF-bg-[--yF-hover-color] yF-cursor-pointer"
						)}
					>
						Confirm Consultation
					</button>
				</div>
			)}
		</div>
	);
}

export default memo(DetailsForm);
